import { useCallback, useEffect, useRef, useState } from "react";

const INTERVAL_MS = 5000;

type UseRichPreviewAutoProgressResult = {
  /** 0 = overview/hero, 1..N = stop index (1-based) */
  activeIndex: number;
  /** Call on mouseenter to pause */
  pause: () => void;
  /** Call on mouseleave to resume */
  resume: () => void;
  isPaused: boolean;
};

/**
 * Timer-based auto-progression for the rich launcher.
 * Cycles 0 → 1 → … → stopCount → 0 (loops).
 * Pauses on hover.
 */
export const useRichPreviewAutoProgress = (
  stopCount: number,
): UseRichPreviewAutoProgressResult => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    if (stopCount <= 0) return;
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev >= stopCount ? 0 : prev + 1));
    }, INTERVAL_MS);
  }, [clear, stopCount]);

  useEffect(() => {
    if (!isPaused && stopCount > 0) {
      start();
    } else {
      clear();
    }
    return clear;
  }, [isPaused, start, clear, stopCount]);

  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);

  return { activeIndex, pause, resume, isPaused };
};
